import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { ArrowLeft, Flag, Trash2, Save, X, Merge, Loader2, CheckCircle, Download, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Registration } from '../types';

interface CountryGroup {
  id: string;
  countryCode: string;
  count: number;
  registrations: Registration[];
}

interface MergeOperation {
  from: string;
  to: string;
  count: number;
}

export default function ManageCountries() {
  const { signOut } = useAuth();
  const [countries, setCountries] = useState<CountryGroup[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<CountryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
  const [canonicalCountry, setCanonicalCountry] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState<{ current: number; total: number } | null>(null);
  const [mergeResults, setMergeResults] = useState<MergeOperation[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCountries();
  }, []);

  // Filter countries based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter(country =>
        country.countryCode.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [searchTerm, countries]);

  const fetchCountries = async () => {
    try {
      const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
      const registrations = registrationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (Registration & { id: string })[];
      
      // Group registrations by country code
      const countryMap = new Map<string, Registration[]>();
      registrations.forEach(reg => {
        const country = reg.countryCode || 'Unknown';
        if (!countryMap.has(country)) {
          countryMap.set(country, []);
        }
        countryMap.get(country)!.push(reg);
      });

      // Convert to array and sort by count (most registrations first)
      const countryGroups: CountryGroup[] = Array.from(countryMap.entries()).map(([country, regs]) => ({
        id: country, // Use country code as ID
        countryCode: country,
        count: regs.length,
        registrations: regs
      }));

      countryGroups.sort((a, b) => a.countryCode.localeCompare(b.countryCode));
      setCountries(countryGroups);
      setFilteredCountries(countryGroups);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setLoading(false);
    }
  };

  const handleCountrySelection = (countryCode: string, checked: boolean) => {
    const newSelected = new Set(selectedCountries);
    if (checked) {
      newSelected.add(countryCode);
    } else {
      newSelected.delete(countryCode);
    }
    setSelectedCountries(newSelected);
    
    // Reset canonical country if it's no longer in selection
    if (!newSelected.has(canonicalCountry)) {
      setCanonicalCountry(newSelected.size > 0 ? Array.from(newSelected)[0] : '');
    }
  };

  const handleDeleteAll = async (countryCode: string, count: number) => {
    if (!window.confirm(`Er du sikker på at du vil slette ALLE ${count} registreringer for land "${countryCode}"? Dette kan ikke angres.`)) {
      return;
    }

    try {
      // Find all registrations with this country code
      const registrationsSnapshot = await getDocs(
        query(collection(db, 'registrations'), where('countryCode', '==', countryCode))
      );

      // Delete all registrations in batches
      const batch = writeBatch(db);
      registrationsSnapshot.docs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });

      await batch.commit();
      
      // Refresh the countries list
      await fetchCountries();
      
      alert(`${count} registreringer for "${countryCode}" ble slettet.`);
    } catch (error) {
      console.error('Error deleting all registrations:', error);
      alert('Feil ved sletting av registreringer');
    }
  };

  const executeMerge = async () => {
    if (selectedCountries.size < 1) {
      alert('Velg minst 1 land for å slå sammen');
      return;
    }

    if (!canonicalCountry) {
      alert('Velg et kanonisk land');
      return;
    }

    const countriesToMerge = Array.from(selectedCountries).filter(name => name !== canonicalCountry);
    
    if (countriesToMerge.length === 0) {
      alert('Ingen land å slå sammen');
      return;
    }

    if (!confirm(`Er du sikker på at du vil slå sammen ${countriesToMerge.length} land til "${canonicalCountry}"? Dette kan ikke angres.`)) {
      return;
    }

    setMerging(true);
    setMergeProgress({ current: 0, total: countriesToMerge.length });
    const results: MergeOperation[] = [];

    try {
      let processed = 0;
      
      // Process merges in batches
      const batchSize = 10;
      
      for (let i = 0; i < countriesToMerge.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchCountries = countriesToMerge.slice(i, i + batchSize);
        
        for (const fromCountry of batchCountries) {
          // Find all registrations with the old country code
          const registrationsSnapshot = await getDocs(
            query(collection(db, 'registrations'), where('countryCode', '==', fromCountry))
          );
          
          const count = registrationsSnapshot.docs.length;
          if (count > 0) {
            // Update all registrations
            registrationsSnapshot.docs.forEach(docSnapshot => {
              batch.update(docSnapshot.ref, { countryCode: canonicalCountry });
            });
            
            results.push({ from: fromCountry, to: canonicalCountry, count });
          }
          
          processed++;
          setMergeProgress({ current: processed, total: countriesToMerge.length });
        }
        
        // Commit the batch
        await batch.commit();
      }

      setMergeResults(results);
      setShowResults(true);
      
      // Refresh the countries list
      await fetchCountries();
      
      // Reset selection state
      setSelectedCountries(new Set());
      setCanonicalCountry('');
      
    } catch (error) {
      console.error('Error executing merge:', error);
      alert('Feil ved sammenslåing av data');
    } finally {
      setMerging(false);
      setMergeProgress(null);
    }
  };

  const downloadReport = () => {
    const csvContent = [
      'Fra,Til,Antall registreringer',
      ...mergeResults.map(result => `${result.from},${result.to},${result.count}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `land_sammenslåing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetResults = () => {
    setShowResults(false);
    setMergeResults([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til dashboard
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Administrer land</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {showResults && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Sammenslåing fullført</h2>
                    <p className="text-gray-600">
                      {mergeResults.length} land ble slått sammen, 
                      totalt {mergeResults.reduce((sum, r) => sum + r.count, 0)} registreringer oppdatert
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={downloadReport}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Last ned rapport
                  </button>
                  <button
                    onClick={resetResults}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Lukk
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Til
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registreringer oppdatert
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mergeResults.map((result, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {result.from}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {result.to}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg font-medium text-gray-900">
                  Land ({countries.length})
                </h2>
                <div className="relative">
                  <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Søk etter land..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {selectedCountries.size > 1 && (
                  <div className="flex items-center space-x-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedCountries.size} land valgt
                    </span>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-blue-900">
                        Slå sammen til:
                      </label>
                      <select
                        value={canonicalCountry}
                        onChange={(e) => setCanonicalCountry(e.target.value)}
                        className="rounded-md border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Velg kanonisk land</option>
                        {Array.from(selectedCountries).map(countryCode => (
                          <option key={countryCode} value={countryCode}>
                            {countryCode}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={executeMerge}
                      disabled={merging || !canonicalCountry || selectedCountries.size < 2}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
                    >
                      {merging ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Slår sammen...
                        </>
                      ) : (
                        <>
                          <Merge className="h-4 w-4 mr-2" />
                          Slå sammen
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Velg
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Land
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Antall registreringer
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCountries.map((country, index) => (
                    <tr key={country.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedCountries.has(country.countryCode)}
                          onChange={(e) => handleCountrySelection(country.countryCode, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{country.countryCode}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {country.count}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDeleteAll(country.countryCode, country.count)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Slett alle ({country.count})
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredCountries.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">
                      {searchTerm ? 'Ingen land funnet' : 'Ingen land funnet'}
                    </p>
                    <p className="mt-2">
                      {searchTerm ? 'Prøv et annet søkeord.' : 'Det er ingen registreringer å vise.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Progress bar for merge operation */}
            {mergeProgress && (
              <div className="px-6 py-4 border-t border-gray-200 bg-blue-50">
                <div className="flex justify-between text-sm text-blue-700 mb-1">
                  <span>Behandler sammenslåinger...</span>
                  <span>{mergeProgress.current} / {mergeProgress.total}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(mergeProgress.current / mergeProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}